import { Button, Divider, Image, Menu, Text } from '@mantine/core';
import {
  IconDownload,
  IconLogout,
  IconMoodEdit,
  IconSettings,
  IconSparkles,
  IconTerminal2,
} from '@tabler/icons-react';
import { memo, ReactNode } from 'react';
import { NavLink, useLocation, useMatch } from 'react-router';
import { useFlags } from 'src/feature-flags/FlagsHook';
import classes from './HeaderRevamp.module.css';

export type HeaderNavItem = {
  title: string;
  href: string;
  activeRegex?: RegExp;
};

type UserAvatarMenuCurrentUser = {
  firstName?: string | null;
  lastName?: string | null;
  id?: string | null;
} | null;

export const HeaderNavLink = memo(function HeaderNavLink({
  title,
  href,
  activeRegex,
}: HeaderNavItem) {
  const isActive = useIsActive(href, activeRegex);

  return (
    <NavLink to={href} className={classes.menuItem} data-active={isActive}>
      <Text size="md" fw={600} px="xxs" py="xxxs" style={{ textWrap: 'nowrap' }}>
        {title}
      </Text>
    </NavLink>
  );
});

export function DrawerNavItem({
  title,
  href,
  activeRegex,
  onSelect,
}: HeaderNavItem & { onSelect: () => void }) {
  const isActive = useIsActive(href, activeRegex);

  return (
    <Button
      variant={isActive ? 'light' : 'subtle'}
      color={isActive ? 'brand' : 'gray'}
      justify="flex-start"
      size="lg"
      fullWidth
      onClick={onSelect}
      styles={{ label: { fontWeight: isActive ? 700 : 500 } }}
    >
      {title}
    </Button>
  );
}

export function DrawerActionItem({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      variant="subtle"
      color="gray"
      justify="flex-start"
      size="md"
      fullWidth
      leftSection={icon}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

export function UserAvatarMenu({
  currentUser,
  isCrawford,
  isSystemAdmin,
  isServiceAdmin,
  onNavigate,
  onLogout,
  onGetApps,
}: {
  currentUser: UserAvatarMenuCurrentUser | undefined;
  isCrawford: boolean;
  isSystemAdmin: boolean;
  isServiceAdmin: boolean;
  onNavigate: (href: string) => void;
  onLogout: () => void;
  onGetApps?: () => void;
}) {
  const { flagEnabledDevTools, flagEnabledAdminAdviserSignatures } = useFlags();
  const showUserManagement = isSystemAdmin || (isServiceAdmin && flagEnabledAdminAdviserSignatures);
  return (
    <Menu
      transitionProps={{ transition: 'pop-top-right' }}
      position="bottom"
      width={220}
      withinPortal
    >
      <Menu.Target>
        <Image
          style={{ width: 40, height: 40 }}
          radius="xl"
          src={
            isCrawford
              ? 'https://public-afternoon-bucket.s3.eu-north-1.amazonaws.com/crawford.jpeg'
              : `https://ui-avatars.com/api/?background=0D8ABC&color=fff&name=${currentUser?.firstName ?? ''}+${currentUser?.lastName ?? ''}`
          }
        />
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Welcome, {currentUser?.firstName}!</Menu.Label>

        <Menu.Item
          leftSection={<IconMoodEdit size={16} />}
          onClick={() => onNavigate(`/user/details/${currentUser?.id}`)}
        >
          My details
        </Menu.Item>

        {onGetApps && (
          <Menu.Item leftSection={<IconDownload size={16} />} onClick={onGetApps}>
            Get apps
          </Menu.Item>
        )}

        <Divider />

        {showUserManagement && (
          <>
            <Menu.Label>Admin settings</Menu.Label>
            <Menu.Item
              leftSection={<IconSettings size={16} />}
              onClick={() => onNavigate('/admin/users')}
            >
              User management
            </Menu.Item>
            {isSystemAdmin && (
              <Menu.Item
                leftSection={<IconSettings size={16} />}
                onClick={() => onNavigate('/admin/data-export-config')}
              >
                Data export configuration
              </Menu.Item>
            )}
            {isSystemAdmin && (
              <Menu.Item
                leftSection={<IconSparkles size={16} />}
                onClick={() => onNavigate('/admin/manage-siesta-skills')}
              >
                Manage Siesta skills
              </Menu.Item>
            )}
            {isSystemAdmin && flagEnabledDevTools && (
              <Menu.Item
                leftSection={<IconTerminal2 size={16} />}
                onClick={() => onNavigate('/admin/dev-tools')}
              >
                Dev tools
              </Menu.Item>
            )}

            <Divider />
          </>
        )}

        <Menu.Item leftSection={<IconLogout size={16} />} onClick={onLogout}>
          Log out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function useIsActive(href: string, activeRegex?: RegExp) {
  const pathMatch = useMatch(href);
  const { pathname } = useLocation();

  if (activeRegex != null && activeRegex.test(pathname)) {
    return true;
  }

  return pathMatch != null;
}
